import { ChangeDetectorRef, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { EchoService } from '@/realtime/echo.service';
import { Utils } from '@/utils/utils';

const STORAGE_KEY = 'tabletIdentity';
const HEARTBEAT_INTERVAL_MS = 20000;

interface TabletIdentity {
    id: number;
    name: string;
    token: string;
}

interface PendingSignRequest {
    document_id: number;
    type: string;
    id_by_company: number;
    client_name: string | null;
    sign_token: string;
}

@Component({
    selector: 'app-tablet-sign',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './tablet-sign.page.html',
})
export class TabletSignPage implements OnInit, OnDestroy {

    identity = signal<TabletIdentity | null>(null);
    pairing = signal(false);
    pendingDocument = signal<PendingSignRequest | null>(null);

    pairForm: FormGroup = new FormGroup({
        code: new FormControl('', [Validators.required]),
        name: new FormControl('', [Validators.required]),
    });

    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

    constructor(
        private apiService: ApiService,
        private echoService: EchoService,
        private router: Router,
        private cdr: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                this.identity.set(JSON.parse(stored));
                this.startListening();
            } catch {
                localStorage.removeItem(STORAGE_KEY);
            }
        }
    }

    ngOnDestroy(): void {
        this.stopListening();
    }

    pair(): void {
        if (!this.pairForm.valid || this.pairing()) {
            Utils.notification.alert('Introduce el código de emparejamiento y un nombre para esta tablet');
            return;
        }

        this.pairing.set(true);
        const { code, name } = this.pairForm.value;

        this.apiService.post(Urls.urls.tabletsPair, { code, name }).subscribe({
            next: (response: any) => {
                const identity: TabletIdentity = { id: response.id, name: response.name, token: response.token };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
                this.identity.set(identity);
                this.pairing.set(false);
                this.startListening();
                this.cdr.detectChanges();
            },
            error: (error: any) => {
                Utils.notification.alert(Utils.extractErrorMessage(error) || 'No se pudo emparejar la tablet');
                this.pairing.set(false);
                this.cdr.detectChanges();
            },
        });
    }

    forgetTablet(): void {
        this.stopListening();
        localStorage.removeItem(STORAGE_KEY);
        this.identity.set(null);
        this.pendingDocument.set(null);
        this.pairForm.reset();
    }

    goToSign(): void {
        const pending = this.pendingDocument();
        if (!pending) return;
        this.pendingDocument.set(null);
        this.router.navigate(['/delivery-note-sign', pending.document_id], {
            queryParams: { token: pending.sign_token },
        });
    }

    private startListening(): void {
        const identity = this.identity();
        if (!identity) return;

        this.heartbeat();
        this.heartbeatTimer = setInterval(() => this.heartbeat(), HEARTBEAT_INTERVAL_MS);

        const channel = this.echoService.tabletChannel(identity.token);

        channel.listen('.document.sign-requested', (data: PendingSignRequest) => {
            this.pendingDocument.set(data);
            this.cdr.detectChanges();
        });

        // Sent by erp-backend's TabletsController::destroy() right before it revokes
        // this tablet - a last message so a still-listening tablet forgets itself
        // instead of sitting paired to a token the admin panel no longer recognizes.
        channel.listen('.tablet.unpaired', () => {
            this.forgetTablet();
            this.cdr.detectChanges();
        });
    }

    private stopListening(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
        const identity = this.identity();
        if (identity) {
            this.echoService.echo.leave('tablet.' + identity.token);
        }
    }

    private heartbeat(): void {
        const identity = this.identity();
        if (!identity) return;

        this.apiService.post(`${Urls.urls.tablets}/${identity.id}/heartbeat`, { token: identity.token }).subscribe({
            error: (error: any) => {
                // Only a 401/403 (TabletsController::heartbeat's "Tablet no válida" - the
                // token no longer matches, e.g. this tablet was revoked from Settings) forgets
                // the session right away. A merely unreachable backend (status 0, or any other
                // transient error) should NOT clear a still-valid paired identity - the next
                // heartbeat retries in HEARTBEAT_INTERVAL_MS instead.
                if (error?.status === 401 || error?.status === 403) {
                    this.forgetTablet();
                    this.cdr.detectChanges();
                }
            },
        });
    }
}
